/**
 * A receipt, on a phone — `docs/visit-page.md` §19.2, §12; Board 16 D
 * (*Review opens a sheet; Approve or Reject*).
 *
 * From the bottom: the photo; the amount, when, who and its chip; what it was
 * for and how it was paid; the internal *against its plan* line; **Pay <name>
 * back**; then **Reject** · **Approve** at 48px. Reject asks why in the sheet
 * itself (§12: *Reject (asks why)*), with the drawn words only.
 */
import { useEffect, useState } from 'react'
import type { ReceiptCard, VisitRead } from '@rowted/shared'
import { Switch } from '../components/primitives.js'
import { COMMON, RECEIPTS } from './copy.js'
import { money, signedMoney, timeIn } from './format.js'
import { Face } from './face.js'
import { VisitButton, VisitPopup, VISIT_LAYER } from './sheet.js'
import { FieldLabel } from './time-money-parts.js'
import { InternalChip } from './internal-chip.js'
import { ReceiptChip, ReceiptPhoto, type ReceiptActions } from './receipt-parts.js'

export function ReceiptSheet({ card, read, actions, onClose }: {
  card: ReceiptCard
  read: VisitRead
  actions: ReceiptActions
  onClose: () => void
}) {
  const [payBack, setPayBack] = useState(card.reimburse)
  const [rejecting, setRejecting] = useState(false)
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  useEffect(() => setPayBack(card.reimburse), [card.reimburse, card.version])

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setProblem(null)
    try {
      await action()
      onClose()
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause))
      setBusy(false)
    }
  }

  const toReview = card.state === 'to_review' && card.canReview
  const paidInSentence = card.paidWith === 'account' ? RECEIPTS.paid.account : RECEIPTS.paidWithInSentence[card.paidWith]

  let footer = null
  if (rejecting) {
    footer = (
      <>
        <VisitButton size="phone" onClick={() => { setRejecting(false); setWhy('') }}>{COMMON.cancel}</VisitButton>
        <VisitButton size="phone" tone="danger" disabled={busy || !why.trim()} onClick={() => void run(() => actions.reject(card, why.trim()))}>
          {RECEIPTS.reject}
        </VisitButton>
      </>
    )
  } else if (toReview) {
    footer = (
      <>
        <VisitButton size="phone" disabled={busy} onClick={() => setRejecting(true)}>{RECEIPTS.reject}</VisitButton>
        <VisitButton size="phone" tone="primary" glyph="check" disabled={busy} onClick={() => void run(() => actions.approve(card, payBack))}>
          {RECEIPTS.approve}
        </VisitButton>
      </>
    )
  } else if (card.state === 'approved' && card.canReview) {
    footer = (
      <VisitButton size="phone" disabled={busy} onClick={() => void run(() => actions.undo(card))}>{RECEIPTS.undoApproval}</VisitButton>
    )
  }

  return (
    <VisitPopup
      width={390}
      phone
      title={card.vendor}
      sub={RECEIPTS.receiptOf(read.reference)}
      onClose={onClose}
      tall
      layer={VISIT_LAYER}
      asDrawn="vp-16 D"
      {...(footer ? { footer } : {})}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3.5">
          <ReceiptPhoto card={card} width={96} height={128} />
          <div className="flex flex-col items-start gap-1">
            {card.internal && <b className="text-[20px]">{money(card.internal.amount)}</b>}
            <span className="text-[12.5px] text-text-muted">{RECEIPTS.at(timeIn(read.timeZone, card.at, 'dateAmpm'), String(card.day))}</span>
            {card.sentBy && (
              <div className="mt-1.5 flex items-center gap-1.5">
                <Face person={card.sentBy} size={22} />
                <span className="text-[13px]">{card.sentBy.name}</span>
              </div>
            )}
            <ReceiptChip card={card} />
          </div>
        </div>

        <div className="rounded-[10px] bg-surface-alt px-3 py-2.5 text-[13.5px] leading-[1.45]">
          {COMMON.dot(card.description, paidInSentence)}
        </div>

        {card.against && card.internal?.planned && (
          <div className="flex items-center gap-2 rounded-[10px] border border-line bg-surface-internal px-3 py-2.5 text-[13px]">
            <InternalChip />
            <span className="min-w-0">{RECEIPTS.againstLineShort(card.against.name, money(card.internal.planned))}</span>
            <span className="flex-1" />
            {card.internal.delta && card.internal.delta.tone !== 'zero' && (
              <b className={card.internal.delta.tone === 'over' ? 'text-warn' : 'text-accent-dark'}>{signedMoney(card.internal.delta)}</b>
            )}
          </div>
        )}

        {toReview && !rejecting && card.sentBy && (
          <button
            type="button"
            role="switch"
            aria-checked={payBack}
            onClick={() => setPayBack((was) => !was)}
            className="flex min-h-11 items-center gap-2.5 rounded-[10px] border border-line p-3 text-left"
          >
            <Switch checked={payBack} onChange={setPayBack} label={RECEIPTS.payBack(card.sentBy.firstName)} hideLabel readOnly />
            <span className="text-[13.5px]"><b>{RECEIPTS.payBack(card.sentBy.firstName)}</b> {RECEIPTS.payBackSub}</span>
          </button>
        )}

        {card.state === 'approved' && card.reviewed && (
          <div className="flex items-center gap-2 rounded-[10px] border border-green-band-line bg-green-band-bg px-3 py-2.5 text-[13px] text-accent-dark">
            {RECEIPTS.approvedBy(card.reviewed.by.name, timeIn(read.timeZone, card.reviewed.at, 'dayAmpm'))}
          </div>
        )}

        {rejecting && (
          <div>
            <FieldLabel htmlFor={`reject-sheet-${card.id}`} required>{COMMON.why}</FieldLabel>
            <textarea id={`reject-sheet-${card.id}`} value={why} autoFocus onChange={(event) => setWhy(event.target.value)}
              className="block h-20 w-full resize-none rounded-button tabular-nums border border-line bg-surface px-3 py-2.5 text-[15px] leading-normal
                         text-text outline-none focus:border-accent focus:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]" />
          </div>
        )}

        {problem && <p className="m-0 text-[13px] text-danger">{problem}</p>}
      </div>
    </VisitPopup>
  )
}
